import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { 
  Plus, Loader2, AlertTriangle, Shield, Link2, Calendar,
  CheckCircle, AlertCircle, HelpCircle, Eye, EyeOff
} from 'lucide-react';

// Update type options with categories
const UPDATE_TYPE_CATEGORIES = {
  'Investigation': [
    { value: 'poi_identified', label: 'Person of Interest Identified' },
    { value: 'new_lead', label: 'New Lead' },
    { value: 'investigation_expanded', label: 'Investigation Expanded' },
  ],
  'Evidence': [
    { value: 'dna_recovered', label: 'DNA Evidence Recovered' },
    { value: 'blood_recovered', label: 'Blood Evidence Recovered' },
    { value: 'property_recovered', label: 'Property Recovered' },
    { value: 'evidence_analyzed', label: 'Evidence Analyzed' },
  ],
  'Legal': [
    { value: 'arrest', label: 'Arrest Made' },
    { value: 'charges_filed', label: 'Charges Filed' },
    { value: 'indictment', label: 'Indictment' },
    { value: 'plea', label: 'Plea Entered' },
    { value: 'conviction', label: 'Conviction' },
    { value: 'sentencing', label: 'Sentencing' },
    { value: 'appeal', label: 'Appeal Filed' },
  ],
  'Search': [
    { value: 'search_conducted', label: 'Search Conducted' },
    { value: 'search_area_expanded', label: 'Search Area Expanded' },
  ],
  'Recovery': [
    { value: 'remains_found', label: 'Remains Found' },
    { value: 'partial_remains', label: 'Partial Remains Found' },
    { value: 'personal_effects_found', label: 'Personal Effects Found' },
  ],
  'Identification': [
    { value: 'remains_identified', label: 'Remains Identified' },
    { value: 'identity_confirmed', label: 'Identity Confirmed' },
  ],
  'Status Change': [
    { value: 'case_closed', label: 'Case Closed' },
    { value: 'case_reclassified', label: 'Case Reclassified' },
    { value: 'case_reopened', label: 'Case Reopened' },
    { value: 'found_safe', label: 'Found Safe' },
    { value: 'found_deceased', label: 'Found Deceased' },
  ],
  'Corrections': [
    { value: 'detail_corrected', label: 'Detail Corrected' },
    { value: 'info_retracted', label: 'Information Retracted' },
  ],
  'Media': [
    { value: 'media_coverage', label: 'Media Coverage' },
    { value: 'public_appeal', label: 'Public Appeal' },
    { value: 'reward_announced', label: 'Reward Announced' },
  ],
  'Other': [
    { value: 'other', label: 'Other Update' },
  ],
};

const SOURCE_TYPES = [
  { value: 'law_enforcement', label: 'Law Enforcement Statement' },
  { value: 'court_document', label: 'Court Document' },
  { value: 'news_major', label: 'Major News Outlet' },
  { value: 'news_local', label: 'Local News' },
  { value: 'press_release', label: 'Press Release' },
  { value: 'family_statement', label: 'Family Statement' },
  { value: 'official_website', label: 'Official Website (NamUs, NCMEC, etc.)' },
  { value: 'social_media_official', label: 'Official Social Media' },
  { value: 'social_media_other', label: 'Social Media (Other)' },
  { value: 'community_tip', label: 'Community Tip' },
  { value: 'other', label: 'Other Source' },
];

const CONFIDENCE_LEVELS = [
  { value: 'verified', label: 'Verified', icon: CheckCircle, color: 'text-green-600', description: 'Confirmed by LE/court' },
  { value: 'reported', label: 'Reported', icon: AlertCircle, color: 'text-yellow-600', description: 'From reputable news' },
  { value: 'unconfirmed', label: 'Unconfirmed', icon: HelpCircle, color: 'text-gray-500', description: 'Not yet verified' },
];

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public', icon: Eye, description: 'Visible to everyone' },
  { value: 'restricted', label: 'Restricted', icon: EyeOff, description: 'Logged-in users only' },
  { value: 'verified_only', label: 'Verified Only', icon: Shield, description: 'Verified/Pro users only' },
  { value: 'internal', label: 'Internal', icon: Shield, description: 'Admin/staff only' },
];

export default function AddCaseUpdateForm({ caseId, caseName, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    update_type: '',
    date_of_update: new Date().toISOString().split('T')[0],
    summary: '',
    details: '',
    source_type: '',
    source_link: '',
    source_title: '',
    source_date: '',
    confidence_level: 'reported',
    visibility: 'public',
    safety_wording_flag: false,
    contains_sensitive_info: false,
  });
  
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.update_type) {
      toast.error('Please select an update type');
      return;
    }
    if (!formData.summary || formData.summary.length < 10) {
      toast.error('Summary must be at least 10 characters');
      return;
    }
    if (!formData.source_type) {
      toast.error('Please select a source type');
      return;
    }
    if (!formData.source_link || !formData.source_link.startsWith('http')) {
      toast.error('Please provide a valid source URL');
      return;
    }
    if (!formData.date_of_update) {
      toast.error('Please provide the date of the update');
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        case_id: caseId,
        ...formData
      };
      
      const response = await api.post('/case-updates', payload);
      
      toast.success(response.data.message || 'Update added successfully');
      
      if (onSuccess) {
        onSuccess(response.data.update);
      }
    } catch (error) {
      console.error('Error adding update:', error);
      toast.error(error.response?.data?.detail || 'Failed to add update');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Case Reference */}
      <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
        <p className="text-sm text-muted-foreground">Adding update for:</p>
        <p className="font-semibold">{caseName || caseId}</p>
      </div>
      
      {/* Update Type */}
      <div>
        <Label htmlFor="update_type">Update Type *</Label>
        <Select value={formData.update_type} onValueChange={(v) => updateField('update_type', v)}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select update type" />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {Object.entries(UPDATE_TYPE_CATEGORIES).map(([category, types]) => (
              <div key={category}>
                <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-slate-50 dark:bg-slate-800">
                  {category}
                </div>
                {types.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Date of Update */}
      <div>
        <Label htmlFor="date_of_update">Date of Update *</Label>
        <Input
          id="date_of_update"
          type="date"
          value={formData.date_of_update}
          onChange={(e) => updateField('date_of_update', e.target.value)}
          className="mt-1"
        />
        <p className="text-xs text-muted-foreground mt-1">When did this update become public?</p>
      </div>
      
      {/* Summary */}
      <div>
        <Label htmlFor="summary">Summary * (1-2 sentences)</Label>
        <Textarea
          id="summary"
          value={formData.summary}
          onChange={(e) => updateField('summary', e.target.value)}
          placeholder="Brief summary of the update..."
          className="mt-1"
          rows={2}
          maxLength={500}
        />
        <p className="text-xs text-muted-foreground mt-1">{formData.summary.length}/500 characters</p>
      </div>
      
      {/* Details */}
      <div>
        <Label htmlFor="details">Details (Optional)</Label>
        <Textarea
          id="details"
          value={formData.details}
          onChange={(e) => updateField('details', e.target.value)}
          placeholder="Additional details, context, or full text..."
          className="mt-1"
          rows={4}
        />
      </div>
      
      {/* Source Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="w-4 h-4" />
            Source Information
          </CardTitle>
          <CardDescription>Required for all updates</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="source_type">Source Type *</Label>
            <Select value={formData.source_type} onValueChange={(v) => updateField('source_type', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select source type" />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="source_link">Source URL *</Label>
            <Input
              id="source_link"
              value={formData.source_link}
              onChange={(e) => updateField('source_link', e.target.value)}
              placeholder="https://..."
              className="mt-1"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="source_title">Source Title</Label>
              <Input
                id="source_title"
                value={formData.source_title}
                onChange={(e) => updateField('source_title', e.target.value)}
                placeholder="Article or document title"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="source_date">Source Date</Label>
              <Input
                id="source_date"
                type="date"
                value={formData.source_date}
                onChange={(e) => updateField('source_date', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Verification & Visibility */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Verification & Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Confidence Level */}
          <div>
            <Label>Confidence Level</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {CONFIDENCE_LEVELS.map(level => {
                const Icon = level.icon;
                const isSelected = formData.confidence_level === level.value;
                return (
                  <button
                    key={level.value}
                    type="button"
                    onClick={() => updateField('confidence_level', level.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isSelected 
                        ? 'border-trust-blue-500 bg-trust-blue-50 dark:bg-trust-blue-950/30' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className={`flex items-center gap-2 ${level.color}`}>
                      <Icon className="w-4 h-4" />
                      <span className="font-medium text-sm">{level.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{level.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Visibility */}
          <div>
            <Label>Visibility</Label>
            <Select value={formData.visibility} onValueChange={(v) => updateField('visibility', v)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {VISIBILITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <div className="flex items-center gap-2">
                      <opt.icon className="w-4 h-4" />
                      <span>{opt.label}</span>
                      <span className="text-xs text-muted-foreground">- {opt.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Safety Flags */}
      <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-4 h-4" />
            Safety Flags
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="safety_wording_flag">Contains Allegations/Claims</Label>
              <p className="text-xs text-muted-foreground">
                Enable if this update includes unverified statements or accusations
              </p>
            </div>
            <Switch
              id="safety_wording_flag"
              checked={formData.safety_wording_flag}
              onCheckedChange={(checked) => updateField('safety_wording_flag', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="contains_sensitive_info">Contains Sensitive Info</Label>
              <p className="text-xs text-muted-foreground">
                Enable if this includes addresses, minors, or explicit details
              </p>
            </div>
            <Switch
              id="contains_sensitive_info"
              checked={formData.contains_sensitive_info}
              onCheckedChange={(checked) => updateField('contains_sensitive_info', checked)}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading} className="bg-trust-blue-600 hover:bg-trust-blue-700">
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" />
              Add Update
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
